import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey } from '@fortawesome/pro-light-svg-icons/faKey';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import copy from 'copy-to-clipboard';
import { TypedMemo } from '../../../common/types';
import { CreatedApiToken } from './useApiAccessTokens';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the API tab; each part uses its own slice of it */
import styles from './apiAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ApiAccessRevealTokenProps {
  created: CreatedApiToken;
  onDismiss: () => void;
}

/**
 * The token, shown exactly once. `create` never returns the plaintext again, so
 * this is the only chance to copy it — the warning and the copy affordance have
 * to be impossible to miss.
 */
const ApiAccessRevealTokenComponent: React.FC<ApiAccessRevealTokenProps> = ({ created, onDismiss }) => {
  const intl = useIntl();

  return (
    <Panel as="section" tone="brand" className={styles.reveal}>
      <PanelHead
        lead={
          <span className={styles.revealIcon}>
            <FontAwesomeIcon icon={faKey} />
          </span>
        }
        title={intl.formatMessage({ id: 'app.sml.settings.apiAccess.reveal.created', defaultMessage: 'Token created' })}
        right={<span className={styles.revealName}>{created.name}</span>}
      />
      <PanelBody>
        <p className={styles.revealBody}>
          {intl.formatMessage({
            id: 'app.sml.settings.apiAccess.reveal.warning',
            defaultMessage: 'This is the only time the full token is shown. Copy it now: you will not see it again.',
          })}
        </p>

        {/* Clarity records static text in a session replay. The token must never reach it. */}
        <div className={styles.revealKey} data-clarity-mask="true">
          <div className={styles.revealField}>
            <code>{created.token}</code>
          </div>
          <Button color="primary" onClick={() => copy(created.token)}>
            <FontAwesomeIcon icon={faCopy} className="me-2" />
            {intl.formatMessage({ id: 'app.sml.settings.apiAccess.reveal.copy', defaultMessage: 'Copy token' })}
          </Button>
        </div>
      </PanelBody>
      <PanelFoot className={styles.revealFoot}>
        <span className={styles.revealWarn}>
          <FontAwesomeIcon icon={faLock} />
          {intl.formatMessage({
            id: 'app.sml.settings.apiAccess.reveal.treatAsPassword',
            defaultMessage:
              'Treat it like a password. Anyone who has it can read and change the data of this workspace as you, and open its billing if you own it. It cannot change your email, make new tokens or delete your account.',
          })}
        </span>
        <Button color="light" onClick={onDismiss}>
          {intl.formatMessage({ id: 'app.sml.settings.apiAccess.reveal.dismiss', defaultMessage: 'I have stored it' })}
        </Button>
      </PanelFoot>
    </Panel>
  );
};

export const ApiAccessRevealToken = TypedMemo(ApiAccessRevealTokenComponent);
